import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import AdminAPI from './AdminAPI';
import {
  adminContentFilterSearch,
  partitionAdminContent,
} from './adminContentFilter';
import { validateAdminContent } from './adminContentValidation';

const read = (value, camelName, pascalName) => (
  value?.[camelName] ?? value?.[pascalName]
);

const id = value => String(read(value, 'id', 'Id') || '');
const name = value => String(read(value, 'name', 'Name') || id(value));

function Breakdown({ rows, title }) {
  const titleId = `admin-overview-${title.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <section className="admin-overview__breakdown" aria-labelledby={titleId}>
      <h2 id={titleId}>{title}</h2>
      <div className="admin-overview__table" role="table" aria-label={title}>
        <div className="admin-overview__table-row admin-overview__table-header" role="row">
          <span role="columnheader">Category</span>
          <span role="columnheader">Matches</span>
          <span role="columnheader">Global / Uncategorized</span>
          <span role="columnheader">Outside</span>
        </div>
        {rows.map(row => (
          <div className="admin-overview__table-row" role="row" key={row.key}>
            <span role="cell">{row.label}</span>
            <span role="cell">{row.counts.match}</span>
            <span role="cell">{row.counts.uncategorized}</span>
            <span role="cell">{row.counts.outside}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function breakdownRows(definitions, records, groups, recordKey, definitionKey) {
  const knownKeys = new Set(definitions.map(definitionKey));
  const rows = definitions.map(definition => {
    const key = definitionKey(definition);
    return {
      key: key || name(definition),
      label: name(definition),
      counts: {
        match: groups.match.filter(record => recordKey(record) === key).length,
        uncategorized: groups.uncategorized
          .filter(record => recordKey(record) === key).length,
        outside: groups.outside.filter(record => recordKey(record) === key).length,
      },
    };
  });
  const unknown = records.filter(record => !knownKeys.has(recordKey(record)));
  if (unknown.length) {
    rows.push({
      key: 'uncategorized-category',
      label: 'Uncategorized',
      counts: {
        match: groups.match.filter(record => !knownKeys.has(recordKey(record))).length,
        uncategorized: groups.uncategorized
          .filter(record => !knownKeys.has(recordKey(record))).length,
        outside: groups.outside
          .filter(record => !knownKeys.has(recordKey(record))).length,
      },
    });
  }
  return rows;
}

export default function AdminContentOverview({ filter }) {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const values = await Promise.all([
        AdminAPI.getRegions(),
        AdminAPI.getLevels(),
        AdminAPI.getEnemies(),
        AdminAPI.getEnemyTypes(),
        AdminAPI.getItems(),
        AdminAPI.getItemTypes(),
        AdminAPI.getProfessions(),
      ]);
      if (values.some(value => !Array.isArray(value))) {
        throw new Error('One or more catalogs could not be loaded.');
      }
      const [
        regions,
        levels,
        enemies,
        enemyTypes,
        items,
        itemTypes,
        professions,
      ] = values;
      setCatalog({
        regions,
        levels,
        enemies,
        enemyTypes,
        items,
        itemTypes,
        professions,
      });
    } catch (loadError) {
      setCatalog(null);
      setError(loadError?.message || 'The content overview could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const overview = useMemo(() => {
    if (!catalog) return null;
    const context = {
      regions: catalog.regions,
      enemies: catalog.enemies,
      items: catalog.items,
    };
    const recipes = catalog.items.filter(item => (
      Boolean(read(item, 'isCraftable', 'IsCraftable'))
      && String(read(item, 'professionKey', 'ProfessionKey') || '').trim()
    ));
    const groups = {
      regions: partitionAdminContent(catalog.regions, 'region', filter, context),
      levels: partitionAdminContent(catalog.levels, 'level', filter, context),
      enemies: partitionAdminContent(catalog.enemies, 'enemy', filter, context),
      items: partitionAdminContent(catalog.items, 'item', filter, context),
      recipes: partitionAdminContent(recipes, 'recipe', filter, context),
    };
    return {
      empty: !(
        catalog.regions.length
        || catalog.levels.length
        || catalog.enemies.length
        || catalog.items.length
      ),
      groups,
      issues: validateAdminContent(catalog),
      summaries: [
        ['Regions', groups.regions],
        ['Levels', groups.levels],
        ['Enemies', groups.enemies],
        ['Items', groups.items],
        ['Recipes', groups.recipes],
      ],
      itemRows: breakdownRows(
        [...catalog.itemTypes].sort((left, right) => name(left).localeCompare(name(right), 'en')),
        catalog.items,
        groups.items,
        item => String(read(item, 'typeId', 'TypeId') || ''),
        id,
      ),
      enemyRows: breakdownRows(
        [...catalog.enemyTypes].sort((left, right) => name(left).localeCompare(name(right), 'en')),
        catalog.enemies,
        groups.enemies,
        enemy => String(read(enemy, 'enemyType', 'EnemyType') || ''),
        id,
      ),
      recipeRows: breakdownRows(
        [...catalog.professions].sort((left, right) => (
          Number(read(left, 'sortOrder', 'SortOrder') || 0)
          - Number(read(right, 'sortOrder', 'SortOrder') || 0)
          || name(left).localeCompare(name(right), 'en')
        )),
        recipes,
        groups.recipes,
        item => String(read(item, 'professionKey', 'ProfessionKey') || ''),
        profession => String(read(profession, 'key', 'Key') || ''),
      ),
    };
  }, [catalog, filter]);

  const itemLink = itemId => {
    const params = new URLSearchParams(adminContentFilterSearch(filter));
    params.set('itemId', itemId);
    return { pathname: '/admin/Items', search: `?${params.toString()}` };
  };

  return (
    <main className="admin-overview" aria-busy={loading}>
      <header className="admin-overview__header">
        <div>
          <h1>Content overview</h1>
          <p>Catalog coverage for the active Region and level range.</p>
        </div>
      </header>
      {loading ? <p role="status">Loading content overview…</p> : null}
      {error ? (
        <div className="admin-changelog__error" role="alert">
          <p>{error}</p>
          <button onClick={loadCatalog} type="button">Retry</button>
        </div>
      ) : null}
      {!loading && overview ? (
        <>
          {overview.empty ? (
            <p className="admin-overview__empty" role="status">
              No Regions, Levels, Enemies, Items, or Recipes are authored yet.
            </p>
          ) : null}
          <section className="admin-overview__summary" aria-label="Content totals">
            {overview.summaries.map(([label, groups]) => (
              <article className="admin-overview__summary-card" key={label}>
                <h2>{label}</h2>
                <div className="admin-overview__count-grid">
                  <span>Matches</span>
                  <strong>{groups.match.length}</strong>
                  <span>Global / Uncategorized</span>
                  <strong>{groups.uncategorized.length}</strong>
                  <span>Outside</span>
                  <strong>{groups.outside.length}</strong>
                </div>
              </article>
            ))}
          </section>
          {overview.issues.length > 0 ? (
            <section className="admin-overview__issues" aria-labelledby="admin-content-issues">
              <h2 id="admin-content-issues">
                Content issues
                <span>{overview.issues.length}</span>
              </h2>
              <ul>
                {overview.issues.map(issue => (
                  <li key={`${issue.code}:${issue.entityId}`}>
                    <span className="admin-content-warning" aria-hidden="true">!</span>
                    <div>
                      <Link to={itemLink(issue.entityId)}>
                        {issue.entityType}: {issue.entityName}
                      </Link>
                      <p>{issue.message}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          <div className="admin-overview__breakdowns">
            <Breakdown rows={overview.itemRows} title="Items by type" />
            <Breakdown rows={overview.enemyRows} title="Enemies by type" />
            <Breakdown rows={overview.recipeRows} title="Recipes by profession" />
          </div>
        </>
      ) : null}
    </main>
  );
}
